import { useNavigate } from "@tanstack/react-router";
import { pageWindow } from "@/lib/table-state";

/**
 * Shared numbered pager for every paginated list on the site. Writes `page`
 * into the URL (omitted on page 1) so each page is linkable and crawlable.
 */
export function TablePager({
  page,
  totalPages,
  label = "Pages",
}: {
  page: number;
  totalPages: number;
  label?: string;
}) {
  const navigate = useNavigate();
  if (totalPages <= 1) return null;

  const go = (n: number) =>
    navigate({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search: ((prev: any) => ({ ...prev, page: n > 1 ? n : undefined })) as any,
      resetScroll: true,
    });

  const btn =
    "rounded-md border border-border px-3 py-2 text-sm font-medium hover:border-primary hover:text-primary disabled:opacity-40";

  return (
    <nav aria-label={label} className="mt-6 flex flex-wrap items-center justify-center gap-2">
      <button type="button" className={btn} disabled={page <= 1} onClick={() => go(page - 1)}>
        Previous
      </button>
      {pageWindow(page, totalPages).map((n, i) =>
        n === null ? (
          <span key={`gap-${i}`} className="px-1 text-sm text-muted-foreground">
            …
          </span>
        ) : (
          <button
            key={n}
            type="button"
            aria-current={n === page ? "page" : undefined}
            onClick={() => go(n)}
            className={
              n === page
                ? "rounded-md border border-primary bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground"
                : btn
            }
          >
            {n}
          </button>
        ),
      )}
      <button
        type="button"
        className={btn}
        disabled={page >= totalPages}
        onClick={() => go(page + 1)}
      >
        Next
      </button>
    </nav>
  );
}
